import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormPresenceService, FormPresenceViewer } from 'app/services/form-presence.service';
import { UserService } from 'app/services/user.service';
import { debounce } from 'lodash';
import { Subscription } from 'rxjs';

@Component({
	selector: 'app-form-presence',
	templateUrl: './form-presence.component.html',
	styleUrls: ['./form-presence.component.css'],
	standalone: true,
	imports: [CommonModule],
})
export class FormPresenceComponent implements OnInit, OnChanges, OnDestroy {
	@Input() formType: string;
	@Input() formId: string;
	@Input() mode: 'view' | 'edit' = 'view';

	viewers: FormPresenceViewer[] = [];
	readonly maxVisible = 4;

	private subscription: Subscription;
	private clientId: string | null = null;

	constructor(private formPresenceService: FormPresenceService, private userService: UserService) {}

	ngOnInit(): void {
		const user = this.userService.currentUserValue?.user;
		this.clientId = user?.clientId ?? null;
		if (this.formId && this.formType && this.clientId) {
			this.formPresenceService.joinForm(this.clientId, this.formType, this.formId, this.mode);
		}
		this.subscription = this.formPresenceService.viewers.subscribe((viewers) => {
			this.viewers = viewers;
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		debounce;
		if (!this.clientId) {
			return;
		}

		const modeChanged =
			!!changes['mode'] &&
			!changes['mode'].firstChange &&
			changes['mode'].previousValue !== changes['mode'].currentValue;
		const formIdChanged =
			!!changes['formId'] &&
			!changes['formId'].firstChange &&
			changes['formId'].previousValue !== changes['formId'].currentValue;
		const formTypeChanged =
			!!changes['formType'] &&
			!changes['formType'].firstChange &&
			changes['formType'].previousValue !== changes['formType'].currentValue;
		if (!modeChanged && !formIdChanged && !formTypeChanged) {
			return;
		}
		this.formPresenceService.leaveForm();
		if (this.formId && this.formType) {
			this.formPresenceService.joinForm(this.clientId, this.formType, this.formId, this.mode);
		}
	}

	ngOnDestroy(): void {
		this.subscription?.unsubscribe();
		this.formPresenceService.leaveForm();
	}

	get visibleViewers(): FormPresenceViewer[] {
		return this.viewers.slice(0, this.maxVisible);
	}

	get overflowCount(): number {
		return Math.max(0, this.viewers.length - this.maxVisible);
	}

	get overflowTooltip(): string {
		return this.viewers
			.slice(this.maxVisible)
			.map((v) => `${v.firstName} ${v.lastName} (${v.mode === 'edit' ? 'Editing' : 'Viewing'})`)
			.join('\n');
	}

	getInitials(viewer: FormPresenceViewer): string {
		const f = viewer.firstName?.[0] ?? '';
		const l = viewer.lastName?.[0] ?? '';
		return (f + l).toUpperCase() || '?';
	}

	getTooltip(viewer: FormPresenceViewer): string {
		const name = `${viewer.firstName} ${viewer.lastName}`.trim() || 'Unknown';
		const action = viewer.mode === 'edit' ? 'Editing' : 'Viewing';
		return `${name} — ${action}`;
	}

	getAvatarClass(viewer: FormPresenceViewer): string {
		return viewer.mode === 'edit' ? 'presence-avatar presence-avatar--edit' : 'presence-avatar presence-avatar--view';
	}
}
